import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/form-controls";
import { toast } from "@/components/ui/toast";
import { LOCAL_ONLY_NOTICE } from "@/lib/fixture-notice";
import { usePermission } from "@/hooks/use-permission";
import { SETTINGS_ORG_PROFILE, SETTINGS_TIMEZONE_OPTIONS, SETTINGS_COUNTRY_OPTIONS, SETTINGS_NOTIFICATION_PREFS } from "@/data/mock";

/* SET-00 / SET-02 — Settings Home / Notification Preferences.
   Canonical source: prototype/pages/admin/settings.html (no current
   Figma frame located). Per the blueprint, SET-00 sources ONLY this
   page's "Organization" tab and SET-02 sources ONLY its
   "Notifications" tab; the page's other 3 tabs (Security, Billing,
   Integrations) are not sourced by any required screen ID and are
   deliberately NOT implemented here, per this task's "do not invent
   settings categories" instruction. DD-007 (tab/route structure) is
   resolved the same way as DD-004/005/006: query-param tabs on one
   route (`?tab=organization|notifications`). Every "Save" action here
   is local-state-only — there is no org-profile or notification-
   preference backend; this matches the canonical source's own
   frontend-only `toast(...)` calls. */

type Tab = "organization" | "notifications";

const TAB_LABELS: Record<Tab, string> = {
  organization: "Organization",
  notifications: "Notifications",
};

export function Settings({ initialTab }: { initialTab: Tab }) {
  const [tab, setTab] = useState<Tab>(initialTab);
  const canEdit = usePermission("settings:full");

  const [orgName, setOrgName] = useState(SETTINGS_ORG_PROFILE.orgName);
  const [legalName, setLegalName] = useState(SETTINGS_ORG_PROFILE.legalName);
  const [primaryDomain, setPrimaryDomain] = useState(SETTINGS_ORG_PROFILE.primaryDomain);
  const [timezone, setTimezone] = useState(SETTINGS_ORG_PROFILE.timezone);
  const [country, setCountry] = useState(SETTINGS_ORG_PROFILE.country);
  const [logoName, setLogoName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [prefs, setPrefs] = useState<Record<string, boolean>>(
    Object.fromEntries(SETTINGS_NOTIFICATION_PREFS.flatMap((g) => g.items.map((i) => [i.key, i.defaultChecked])))
  );

  function onLogoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoName(file.name);
    toast({ variant: "success", title: "Logo staged. Save Changes to apply." });
  }

  function saveOrg() {
    toast({ variant: "success", title: "Settings saved!", description: LOCAL_ONLY_NOTICE });
  }

  function savePrefs() {
    toast({ variant: "success", title: "Notification preferences saved!", description: LOCAL_ONLY_NOTICE });
  }

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-xl font-extrabold text-ink">Settings</h1>
      </div>

      <div className="grid grid-cols-1 gap-8 md:grid-cols-[200px_1fr]">
        <nav className="flex gap-1.5 overflow-x-auto md:flex-col md:overflow-visible">
          {(Object.keys(TAB_LABELS) as Tab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`shrink-0 rounded-md px-3 py-2 text-left text-sm font-medium md:w-full ${
                tab === t ? "bg-primary-soft font-semibold text-primary" : "text-text hover:bg-bg"
              }`}
            >
              {TAB_LABELS[t]}
            </button>
          ))}
        </nav>

        <div>
          {tab === "organization" && (
            <div>
              <h2 className="mb-1 text-lg font-extrabold text-ink">Organization</h2>
              <p className="mb-7 text-sm text-muted">General settings for Harbor Trust Bank, N.A.</p>

              <div className="mb-5 grid gap-4 sm:grid-cols-2">
                <Field label="Organization Name">
                  <TextInput value={orgName} onChange={setOrgName} readOnly={!canEdit} />
                </Field>
                <Field label="Legal Name">
                  <TextInput value={legalName} onChange={setLegalName} readOnly={!canEdit} />
                </Field>
              </div>

              <Field label="Organization ID" hint="This ID cannot be changed after onboarding." className="mb-5">
                <TextInput value={SETTINGS_ORG_PROFILE.orgId} onChange={() => {}} readOnly mono />
              </Field>

              <div className="mb-5 grid gap-4 sm:grid-cols-2">
                <Field label="Primary Domain">
                  <TextInput value={primaryDomain} onChange={setPrimaryDomain} readOnly={!canEdit} />
                </Field>
                <Field label="Timezone">
                  <Select value={timezone} onChange={(e) => setTimezone(e.target.value)} disabled={!canEdit} className="w-full">
                    {SETTINGS_TIMEZONE_OPTIONS.map((tz) => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </Select>
                </Field>
              </div>

              <Field label="Country" className="mb-5 sm:w-1/2 sm:pr-2">
                <Select value={country} onChange={(e) => setCountry(e.target.value)} disabled={!canEdit} className="w-full">
                  {SETTINGS_COUNTRY_OPTIONS.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </Select>
              </Field>

              <Field label="Organization Logo" className="mb-6">
                <button
                  type="button"
                  disabled={!canEdit}
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full rounded-md border-2 border-dashed border-line bg-bg p-6 text-center hover:border-primary hover:bg-primary-soft disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <div className="mb-2 text-2xl">{logoName ? "🖼" : "🏛"}</div>
                  {logoName ? (
                    <div className="text-xs font-bold text-success">{logoName} selected</div>
                  ) : (
                    <div className="text-sm font-semibold text-text">Click to upload logo</div>
                  )}
                  <div className="mt-1 text-xs text-muted">{logoName ? "Click to replace logo" : "PNG or SVG, max 512KB"}</div>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  aria-label="Organization logo"
                  accept="image/png,image/svg+xml"
                  className="hidden"
                  onChange={onLogoChange}
                />
              </Field>

              {canEdit && <Button onClick={saveOrg}>Save Changes</Button>}
            </div>
          )}

          {tab === "notifications" && (
            <div>
              <h2 className="mb-1 text-lg font-extrabold text-ink">Notifications</h2>
              <p className="mb-7 text-sm text-muted">Configure email alerts sent to administrators.</p>

              {SETTINGS_NOTIFICATION_PREFS.map((group) => (
                <div key={group.group} className="mb-5">
                  <div className="mb-3 text-sm font-bold text-ink">{group.group}</div>
                  <div className="divide-y divide-line">
                    {group.items.map((item) => (
                      <label key={item.key} className="flex items-start gap-2.5 py-2.5">
                        <Checkbox
                          checked={!!prefs[item.key]}
                          onCheckedChange={(v) => canEdit && setPrefs((prev) => ({ ...prev, [item.key]: v }))}
                          ariaLabel={item.label}
                          disabled={!canEdit}
                        />
                        <div>
                          <div className="text-sm font-medium text-ink">{item.label}</div>
                          <div className="text-xs text-muted">{item.desc}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}

              {canEdit && <Button className="mt-2" onClick={savePrefs}>Save Preferences</Button>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TextInput({ value, onChange, readOnly, mono }: { value: string; onChange: (v: string) => void; readOnly?: boolean; mono?: boolean }) {
  return (
    <input
      type="text"
      value={value}
      readOnly={readOnly}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full rounded-md border px-3 py-2 text-sm outline-none focus:border-primary focus:shadow-[0_0_0_3px_var(--ads-primary-soft)] ${
        readOnly ? "cursor-default border-line bg-bg text-muted" : "border-line-strong bg-surface text-ink"
      } ${mono ? "font-mono" : ""}`}
    />
  );
}

function Field({ label, hint, className, children }: { label: string; hint?: string; className?: string; children: React.ReactNode }) {
  return (
    <div className={className}>
      <label className="mb-1.5 block text-sm font-semibold text-ink">{label}</label>
      {children}
      {hint && <div className="mt-1 text-xs text-muted">{hint}</div>}
    </div>
  );
}
